import React, { useState, useEffect } from 'react';
import './ProcessingTimeEstimator.css';

export const OFFICIAL_SNAPSHOT_CHECKED_ON = '2026-09-01';

export const OFFICIAL_PROCESSING_SNAPSHOTS = {
  family: {
    categoryA: {
      label: 'Join family - Sponsor Category A / Irish citizen',
      queueDate: '2024-04-29',
      source: 'Immigration Service Delivery Dublin visa-office queue',
      sourceUrl: 'https://www.irishimmigration.ie/visa-decisions/',
      scopeNote: 'Use this date only if the Dublin Visa Office is processing your application.'
    },
    categoryB: {
      label: 'Join family - Sponsor Category B',
      queueDate: '2024-04-16',
      source: 'Immigration Service Delivery Dublin visa-office queue',
      sourceUrl: 'https://www.irishimmigration.ie/visa-decisions/',
      scopeNote: 'Use this date only if the Dublin Visa Office is processing your application.'
    },
    eu: {
      label: 'Family member of an EU/EEA/Swiss citizen',
      queueDate: '2026-03-01',
      source: 'Immigration Service Delivery Dublin visa-office queue',
      sourceUrl: 'https://www.irishimmigration.ie/visa-decisions/',
      scopeNote: 'Use this date only if the Dublin Visa Office is processing your application.'
    }
  },
  criticalSkills: {
    permit: {
      label: 'Critical Skills Employment Permit application',
      queueDate: '2026-08-24',
      source: 'Department of Enterprise, Tourism and Employment queue',
      sourceUrl: 'https://enterprise.gov.ie/en/what-we-do/workplace-and-skills/employment-permits/current-application-processing-dates/current-processing-dates-for-employment-permits.html',
      scopeNote: 'This is the employment-permit queue, not an entry-visa decision queue.'
    },
    entryVisa: {
      label: 'Employment entry visa (after permit)',
      queueDate: '2026-06-24',
      source: 'Immigration Service Delivery Dublin visa-office queue',
      sourceUrl: 'https://www.irishimmigration.ie/visa-decisions/',
      scopeNote: 'Use this date only if the Dublin Visa Office is processing your entry-visa application.'
    }
  }
};

export const getQueueOutcome = ({ applicationDate, queueDate }) => {
  const submitted = new Date(`${applicationDate}T00:00:00`);
  const snapshot = new Date(`${queueDate}T00:00:00`);
  const daysPassed = Math.max(0, Math.floor((Date.now() - submitted.getTime()) / (1000 * 60 * 60 * 24)));
  const isReached = submitted <= snapshot;
  return {
    daysPassed,
    isReached,
    queueDate,
    queuePosition: isReached ? 'At or before the published queue date' : 'After the published queue date'
  };
};

const formatDate = (date) => new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
});

const ProcessingTimeEstimator = ({ onUpgrade, locked = false, hasPremiumAccess = false }) => {
  const [visaType, setVisaType] = useState('');
  const [applicationDate, setApplicationDate] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [familyCategory, setFamilyCategory] = useState('categoryA');
  const [criticalSkillsStage, setCriticalSkillsStage] = useState('permit');

  const visaTypes = [
    { value: 'study', label: 'Study Visa', minWeeks: 8, maxWeeks: 12, avgDays: 70 },
    { value: 'work', label: 'Work Permit', minWeeks: 6, maxWeeks: 10, avgDays: 56 },
    { value: 'tourist', label: 'Tourist Visa', minWeeks: 4, maxWeeks: 8, avgDays: 42 },
    { value: 'family', label: 'Family Reunification', mode: 'queue' },
    { value: 'critical-skills', label: 'Critical Skills route', mode: 'queue' },
    { value: 'join-family', label: 'Join Family Member', minWeeks: 8, maxWeeks: 12, avgDays: 70 }
  ];

  const calculateEstimate = () => {
    if (!visaType || !applicationDate) return;

    const selectedVisa = visaTypes.find(v => v.value === visaType);
    if (!selectedVisa) return;

    const appDate = new Date(`${applicationDate}T00:00:00`);
    const today = new Date();
    const daysPassed = Math.floor((today - appDate) / (1000 * 60 * 60 * 24));

    if (selectedVisa.mode === 'queue') {
      const snapshot = selectedVisa.value === 'family'
        ? OFFICIAL_PROCESSING_SNAPSHOTS.family[familyCategory]
        : OFFICIAL_PROCESSING_SNAPSHOTS.criticalSkills[criticalSkillsStage];
      const queueOutcome = getQueueOutcome({ applicationDate, queueDate: snapshot.queueDate });
      setEstimate({
        mode: 'queue',
        visaLabel: snapshot.label,
        applicationDate: formatDate(applicationDate),
        daysPassed: queueOutcome.daysPassed,
        queueDate: formatDate(snapshot.queueDate),
        queueDateRaw: snapshot.queueDate,
        queuePosition: queueOutcome.queuePosition,
        isReached: queueOutcome.isReached,
        source: snapshot.source,
        sourceUrl: snapshot.sourceUrl,
        scopeNote: snapshot.scopeNote,
        stage: queueOutcome.isReached ? 'The published queue has reached your submission date' : 'The published queue has not reached your submission date',
        stageIcon: queueOutcome.isReached ? '⏳' : '🗓️',
        stageColor: queueOutcome.isReached ? 'stage-processing' : 'stage-early'
      });
      setShowResults(true);
      return;
    }
    
    const estimatedDecisionDate = new Date(appDate);
    estimatedDecisionDate.setDate(estimatedDecisionDate.getDate() + selectedVisa.avgDays);
    
    const daysRemaining = Math.max(0, selectedVisa.avgDays - daysPassed);
    const progressPercentage = Math.min(100, Math.round((daysPassed / selectedVisa.avgDays) * 100));

    let stage = 'Early in the illustrative wait';
    let stageIcon = '🔴';
    let stageColor = 'stage-early';
    
    if (progressPercentage >= 80) {
      stage = 'Past most of the illustrative wait';
      stageIcon = '✅';
      stageColor = 'stage-final';
    } else if (progressPercentage >= 50) {
      stage = 'Past the illustrative midpoint';
      stageIcon = '🟡';
      stageColor = 'stage-mid';
    } else if (progressPercentage >= 25) {
      stage = 'Within the illustrative wait';
      stageIcon = '⏰';
      stageColor = 'stage-processing';
    }

    setEstimate({
      mode: 'range',
      visaLabel: selectedVisa.label,
      minWeeks: selectedVisa.minWeeks,
      maxWeeks: selectedVisa.maxWeeks,
      avgDays: selectedVisa.avgDays,
      daysPassed,
      daysRemaining,
      progressPercentage,
      estimatedDecisionDate: estimatedDecisionDate.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      stage,
      stageIcon,
      stageColor
    });

    setShowResults(true);
  };

  useEffect(() => {
    if (visaType && applicationDate) {
      calculateEstimate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visaType, applicationDate, familyCategory, criticalSkillsStage]);

  const triggerUpgrade = () => {
    if (typeof onUpgrade === 'function') onUpgrade();
  };

  const handleVisaTypeChange = (event) => {
    if (locked) {
      triggerUpgrade();
      return;
    }
    setVisaType(event.target.value);
  };

  const handleApplicationDateChange = (event) => {
    if (locked) {
      triggerUpgrade();
      return;
    }
    setApplicationDate(event.target.value);
  };

  const handleReset = () => {
    if (locked) {
      triggerUpgrade();
      return;
    }
    setVisaType('');
    setApplicationDate('');
    setShowResults(false);
    setEstimate(null);
  };

  return (
    <div className="processing-time-estimator">
      <div className="estimator-header">
        <h2>⏱️ How Long Until Your Visa Decision?</h2>
        <p className="estimator-subtext">
          Get an indicative planning result or the latest official queue position
        </p>
      </div>

      <div className="estimator-form">
        <div className="form-group">
          <label htmlFor="visa-type">Visa Type</label>
          <select
            id="visa-type"
            value={visaType}
            onChange={handleVisaTypeChange}
            onFocus={() => { if (locked) triggerUpgrade(); }}
            className="form-select"
          >
            <option value="">Select your visa type...</option>
            {visaTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {visaType === 'family' && (
            <select
              aria-label="Family reunification sponsor category"
              value={familyCategory}
              onChange={(event) => {
                if (locked) {
                  triggerUpgrade();
                  return;
                }
                setFamilyCategory(event.target.value);
              }}
              className="form-select context-select"
            >
              {Object.entries(OFFICIAL_PROCESSING_SNAPSHOTS.family).map(([value, snapshot]) => (
                <option key={value} value={value}>{snapshot.label}</option>
              ))}
            </select>
          )}
          {visaType === 'critical-skills' && (
            <select
              aria-label="Critical Skills processing stage"
              value={criticalSkillsStage}
              onChange={(event) => {
                if (locked) {
                  triggerUpgrade();
                  return;
                }
                setCriticalSkillsStage(event.target.value);
              }}
              className="form-select context-select"
            >
              {Object.entries(OFFICIAL_PROCESSING_SNAPSHOTS.criticalSkills).map(([value, snapshot]) => (
                <option key={value} value={value}>{snapshot.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="application-date">Application Submission Date</label>
          <input
            type="date"
            id="application-date"
            value={applicationDate}
            onChange={handleApplicationDateChange}
            onFocus={() => { if (locked) triggerUpgrade(); }}
            max={new Date().toISOString().split('T')[0]}
            className="form-input"
          />
        </div>
      </div>

      {showResults && estimate && (
        <div className="estimator-results">
          <p className="estimator-disclaimer">
            {estimate.mode === 'queue'
              ? `This is an official queue snapshot checked ${formatDate(OFFICIAL_SNAPSHOT_CHECKED_ON)}, not a decision deadline or promise. Processing depends on the relevant office, category, document checks, and individual circumstances.`
              : 'This is an indicative calendar-day estimate, not an official deadline or promise. Current processing dates vary by office, season, document checks, and individual circumstances.'}
            {' '}Check the official Immigration Service Delivery or employment-permit updates before making travel or financial commitments.
          </p>
          <div className="results-header">
            <div className="result-badge">
              <span className="badge-icon">📊</span>
              <span className="badge-text">Your Estimate</span>
            </div>
            <button onClick={handleReset} className="reset-btn" aria-label="Reset calculator">
              🔄 Recalculate
            </button>
          </div>

          <div className="timeline-section">
            {estimate.mode === 'queue' ? (
              <div className="queue-snapshot">
                <div className="timeline-stats">
                  <div className="stat-card">
                    <div className="stat-label">Your submission</div>
                    <div className="stat-value stat-date">{estimate.applicationDate}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Official queue date</div>
                    <div className="stat-value stat-date">{estimate.queueDate}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Queue position</div>
                    <div className="stat-value stat-queue">{estimate.isReached ? 'Reached' : 'Not reached'}</div>
                  </div>
                </div>
                <p className="queue-explanation">
                  The official source publishes the date of applications currently being considered, not a guaranteed completion date. Your submission is {estimate.queuePosition.toLowerCase()}.
                  {' '}{estimate.scopeNote}
                </p>
              </div>
            ) : (
              <>
                <div className="timeline-stats">
                  <div className="stat-card">
                    <div className="stat-label">Days Passed</div>
                    <div className="stat-value">{estimate.daysPassed}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Days Remaining</div>
                    <div className="stat-value stat-highlight">{estimate.daysRemaining}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Average Timeline</div>
                    <div className="stat-value">{estimate.avgDays} days</div>
                  </div>
                </div>

                <div className="progress-container">
                  <div className="progress-header">
                    <span className="progress-label">Elapsed time vs. illustrative wait</span>
                    <span className="progress-percentage">{estimate.progressPercentage}%</span>
                  </div>
                  <div className="progress-bar-wrapper">
                    <div className="progress-bar-fill" style={{ width: `${estimate.progressPercentage}%` }}></div>
                  </div>
                  <div className="progress-stages">
                    <div className={`stage-marker ${estimate.progressPercentage >= 0 ? 'active' : ''}`}><div className="marker-dot"></div><span className="marker-label">Start</span></div>
                    <div className={`stage-marker ${estimate.progressPercentage >= 33 ? 'active' : ''}`}><div className="marker-dot"></div><span className="marker-label">One third</span></div>
                    <div className={`stage-marker ${estimate.progressPercentage >= 66 ? 'active' : ''}`}><div className="marker-dot"></div><span className="marker-label">Two thirds</span></div>
                    <div className={`stage-marker ${estimate.progressPercentage >= 100 ? 'active' : ''}`}><div className="marker-dot"></div><span className="marker-label">Full estimate</span></div>
                  </div>
                </div>
              </>
            )}

            <div className={`current-stage ${estimate.stageColor}`}>
              <span className="stage-icon">{estimate.stageIcon}</span>
              <div className="stage-info">
                <div className="stage-name">{estimate.stage}</div>
                <div className="stage-detail">
                  {estimate.mode === 'queue'
                    ? <>Official snapshot: <a href={estimate.sourceUrl} target="_blank" rel="noopener noreferrer">{estimate.source}</a></>
                    : <>Estimated decision: <strong>{estimate.estimatedDecisionDate}</strong></>}
                </div>
              </div>
            </div>
          </div>

          <div className="estimate-range">
            <div className="range-info">
              <span className="info-icon">📅</span>
              <span className="range-text">
                {estimate.mode === 'queue'
                    ? <><strong>{estimate.visaLabel}</strong> — published queue date: <strong>{estimate.queueDate}</strong></>
                  : <>Typical {estimate.visaLabel} processing: <strong>{estimate.minWeeks}-{estimate.maxWeeks} weeks</strong></>}
              </span>
            </div>
          </div>

          <div className="cta-section">
            <div className="cta-content">
              <div className="cta-icon">⚡</div>
              <div className="cta-text">
                <h3>Get notified when your decision appears in an official NDVO publication</h3>
                <p>
                  {estimate.mode === 'queue'
                    ? 'Use the official queue update for the latest position; there is no reliable fixed decision date.'
                    : 'Elapsed time does not reveal your application stage or when a decision will arrive. Check the official updates for your visa office.'}
                </p>
                <ul className="cta-benefits">
                  <li>✅ Email alert after our official-file check</li>
                  <li>✅ Optional browser notification backup</li>
                  <li>✅ Access to all 13 premium tools</li>
                </ul>
              </div>
            </div>
            {!hasPremiumAccess && (
              <button className="btn-primary btn-shimmer cta-button" onClick={triggerUpgrade}>
                Unlock Alerts + 13 Tools for ₹349 →
              </button>
            )}
            <p className="cta-footnote">
              One-time ₹349 premium access. Timing depends on the official publication and notification providers.
            </p>
          </div>
        </div>
      )}

      {!showResults && (
        <div className="estimator-placeholder">
          <div className="placeholder-icon">🎯</div>
          <p>Select your visa type and application date to see your personalized timeline</p>
        </div>
      )}
    </div>
  );
};

export default ProcessingTimeEstimator;
